import * as React from 'react'
import Link from '../Link'
import Button from '../Button'
import { ImageElement } from '@kentico/gatsby-kontent-components'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { ContactSection as ContactSectionClass } from './model'
import { ImageQuality } from '../../common/typings/enums'
import { IsMobileContext } from '../../common/Context'
import './style.scss'

type ContactSectionRefs = {
  header: HTMLElement | null,
  image: HTMLElement | null,
}

const ContactSection = React.forwardRef((props: ContactSectionClass, ref: React.Ref<ContactSectionRefs>) => {
  const {
    header,
    name,
    phone,
    email,
    photo,
    videoUrl,
    position,
    linkedIn,
    calendlyText,
    calendlyUrl,
  } = props

  const contactHeaderRef = React.useRef(null)
  const contactImageRef = React.useRef(null)
  React.useImperativeHandle(ref, () => ({
    get header() { return contactHeaderRef.current },
    get image() { return contactImageRef.current }
  }))

  const { t } = useTranslation()
  const { isMobile } = React.useContext(IsMobileContext)
  const imageQualityOptions = React.useMemo(() => ({ quality: ImageQuality.OPTIMIZED }), [])
  const videoStyle = React.useMemo(() => ({ aspectRatio: '1/1' }), [])

  const calendlyClickHandler = () => {
    window.open(calendlyUrl)
  }

  return (
    <section className="ContactSection GlobSectPadSpacing order-1 lg:order-none">
      <div className="SpacingAdjust pt-mobile-l lg:pt-l"></div>
      <div className="grid grid-cols-6 gap-x-m">
        <div ref={contactHeaderRef} className="FontL col-span-6 mb-mobile-s lg:mb-s">
          {header}
        </div>
        <div ref={contactImageRef} className="col-span-6 lg:col-span-4">
          {photo && (
            <ImageElement
              image={photo}
              layout="fullWidth"
              aspectRatio={1}
              alt={photo.description || photo.name}
              options={imageQualityOptions}
            />
          )}
          {videoUrl && (
            <video
              autoPlay muted loop playsInline
              className="Video overflow-hidden flex object-cover"
              style={videoStyle}
            >
              <source
                src={videoUrl}
                type="video/mp4"
              />
            </video>
          )}
        </div>
        <div className="FontL col-span-6 pt-mobile-s lg:pt-s pb-mobile-m lg:pb-m FontM">
          {name && position && (
            <p>{`${name}  —  ${position}`}</p>
          )}
          {phone && (isMobile
            ? <p>{t('common:mobile')}: <a href={`tel: ${phone}`}>{phone}</a></p>
            : <p>{t('common:mobile')}: {phone}</p>
          )}
          {email && (
            <p><a href={`mailto: ${email}`}>{email}</a></p>
          )}
          {linkedIn && (
            <p className="FontS mt-mobile-s lg:mt-s">
              <Link url={linkedIn} title="LinkedIn" />
            </p>
          )}
        </div>
        <div className="col-span-6 lg:col-span-4">
          {calendlyText && calendlyUrl && (
            <Button
              title={calendlyText}
              className="ButtonSecondary FontS w-full"
              onClick={calendlyClickHandler}
            />
          )}
        </div>
      </div>
    </section>
  )
})
ContactSection.displayName = 'ContactSection'

export default ContactSection
